import type {
  MspHealthPrototypeModel,
  PrototypeClientCard,
  PrototypeDestinationSummary,
  PrototypeHealthTone,
  PrototypeIssue,
  PrototypeScoreCard,
} from './model.js';
import type { AuthenticatedUser } from './auth/model.js';
import {
  canAccessNavigation,
  orderedOperationalNavigation,
  type NavigationItem,
} from './auth/permissions.js';
import { renderAccountControl } from './auth/view.js';
import { escapeAttribute, escapeHtml } from './html.js';

export function renderDashboard(
  model: MspHealthPrototypeModel,
  user: AuthenticatedUser,
): string {
  return renderAuthenticatedShell(
    user,
    model.managedClientCount,
    'OVERVIEW',
    `
      <main class="workspace" data-view="overview">
        <header class="page-heading">
          <h1>Overview</h1>
          <p>Operational health, current exceptions, and connected service areas</p>
        </header>
        ${renderOverallSummary(model)}
        ${renderIssues(model.issues)}
        ${renderOperationalAreas(model)}
        <p class="prototype-note">${dashboardDataNote(model)}</p>
      </main>
    `,
  );
}

export function renderBackups(
  model: MspHealthPrototypeModel,
  user: AuthenticatedUser,
): string {
  return renderAuthenticatedShell(
    user,
    model.managedClientCount,
    'BACKUPS',
    `
      <main class="workspace" data-view="backups">
        <header class="page-heading">
          <h1>Backups</h1>
          <p>Observed NinjaOne backup condition, exceptions, and client coverage</p>
        </header>
        ${renderBackupHealthSummary(model)}
        ${renderIssues(model.issues, 'Backup Needs Attention')}
        ${renderBackupClients(model.clients, model.managedClientCount)}
        <p class="prototype-note">${dashboardDataNote(model)}</p>
      </main>
    `,
  );
}

export function renderClientsView(
  model: MspHealthPrototypeModel,
  user: AuthenticatedUser,
): string {
  return renderAuthenticatedShell(
    user,
    model.managedClientCount,
    'CLIENTS',
    `
      <main class="workspace" data-view="clients">
        <header class="page-heading">
          <h1>Clients</h1>
          <p>Managed-client directory and cross-domain operational entry points</p>
        </header>
        ${renderManagedClients(model.clients, model.managedClientCount)}
        <p class="prototype-note">Backup is the only connected health domain · Additional client domains are planned</p>
      </main>
    `,
  );
}

export function renderPlannedModule(
  model: MspHealthPrototypeModel,
  user: AuthenticatedUser,
  activeNavigation: Extract<NavigationItem, 'SERVERS' | 'MICROSOFT_365' | 'THREECX' | 'ONBOARDING' | 'PROJECTS'>,
  title: string,
): string {
  return renderAuthenticatedShell(
    user,
    model.managedClientCount,
    activeNavigation,
    `
      <main class="workspace" data-view="${activeNavigation.toLowerCase()}">
        <header class="page-heading">
          <h1>${escapeHtml(title)}</h1>
          <p>This operational area is planned and is not yet connected to a live data source.</p>
        </header>
        <section class="module-placeholder" aria-label="${escapeAttribute(title)} status">
          <span>Planned</span>
          <h2>Not connected</h2>
          <p>No placeholder score is included in Overall Health.</p>
        </section>
      </main>
    `,
  );
}

/** Global operational surface; tenant connection management belongs to Client Integrations. */
export function renderMicrosoft365Module(
  model: MspHealthPrototypeModel,
  user: AuthenticatedUser,
): string {
  return renderAuthenticatedShell(user, model.managedClientCount, 'MICROSOFT_365', `
    <main class="workspace" data-view="microsoft-365-global">
      <header class="page-heading"><h1>Microsoft 365</h1>
        <p>Operational health across managed client tenants</p></header>
      <section class="module-placeholder" aria-label="Microsoft 365 monitoring status">
        <span>Planned</span><h2>Not connected</h2>
        <p>Tenant health, incidents, and licensing will appear here when the collector is available.</p>
        <p>No Microsoft 365 score is included in Overall Health.</p>
      </section>
    </main>
  `);
}

function dashboardDataNote(model: MspHealthPrototypeModel): string {
  const backup = model.categories.find((category) => category.label === 'Observed Backup Health' || category.label === 'Backup Health');
  return backup?.dataKind === 'LIVE'
    ? 'Backup data from the local NinjaOne baseline · Other health domains remain placeholders'
    : 'Mock presentation data only · No live systems connected';
}

export function renderAuthenticatedShell(
  user: AuthenticatedUser,
  clientCount: number,
  activeNavigation: NavigationItem,
  workspace: string,
): string {
  return `
    <div class="app-shell">
      ${renderTopbar(user)}
      ${renderSidebar(clientCount, user, activeNavigation)}
      ${workspace}
    </div>
  `;
}

function renderTopbar(user: AuthenticatedUser): string {
  return `
    <header class="topbar">
      <div class="product-name">
        <span class="product-logo-mark" aria-hidden="true"><img src="/assets/msp-health-logo.png" alt=""></span>
        <strong>MSP Health</strong><span class="product-divider" aria-hidden="true"></span><small>Admin center</small>
      </div>
      <label class="search-shell">
        <span class="sr-only">Search clients or services</span>
        ${icon('search')}
        <input type="search" placeholder="Search clients or services..." autocomplete="off" />
      </label>
      <div class="operator-context">
        <span class="help-icon" aria-hidden="true">?</span>
        <span class="operator-divider" aria-hidden="true"></span>
        ${renderAccountControl(user)}
      </div>
    </header>
  `;
}

function renderSidebar(
  clientCount: number,
  user: AuthenticatedUser,
  activeNavigation: NavigationItem,
): string {
  const items: Array<{
    permission: NavigationItem;
    href: string;
    label: string;
    iconName: string;
    active?: boolean;
    suffix?: string;
  }> = [
    { permission: 'OVERVIEW', href: '/overview', label: 'Overview', iconName: 'overview' },
    { permission: 'CLIENTS', href: '/clients', label: 'Clients', iconName: 'clients', suffix: String(clientCount) },
    { permission: 'BACKUPS', href: '/backups', label: 'Backups', iconName: 'backup' },
    { permission: 'SERVERS', href: '/servers', label: 'Servers', iconName: 'server' },
    { permission: 'MICROSOFT_365', href: '/m365', label: 'Microsoft 365', iconName: 'cloud' },
    { permission: 'THREECX', href: '/3cx', label: '3CX', iconName: 'phone' },
    { permission: 'ONBOARDING', href: '/onboarding', label: 'Onboarding', iconName: 'onboarding' },
    { permission: 'PROJECTS', href: '/projects', label: 'Projects', iconName: 'projects' },
    { permission: 'SETTINGS', href: '/settings', label: 'Settings', iconName: 'settings' },
    { permission: 'PLATFORM_ADMIN', href: '/platform-admin', label: 'Platform administration', iconName: 'security' },
  ];
  const byPermission = new Map(items.map((item) => [item.permission, item]));
  return `
    <aside class="sidebar">
      <nav aria-label="Primary navigation">
        ${orderedOperationalNavigation(user.operationalProfile)
          .flatMap((permission) => byPermission.get(permission) ?? [])
          .filter((item) => canAccessNavigation(user.role, item.permission))
          .map((item) =>
            navItem(
              item.href,
              item.label,
              item.iconName,
              item.permission === activeNavigation,
              item.suffix,
            ),
          )
          .join('')}
      </nav>
    </aside>
  `;
}

function navItem(
  href: string,
  label: string,
  iconName: string,
  active = false,
  suffix?: string,
): string {
  return `<a class="nav-item${active ? ' active' : ''}" href="${href}" data-app-route${active ? ' aria-current="page"' : ''}>
    ${icon(iconName)}<span>${escapeHtml(label)}</span>${suffix === undefined ? '' : `<small>${escapeHtml(suffix)}</small>`}
  </a>`;
}

function renderOverallSummary(model: MspHealthPrototypeModel): string {
  return `
    <section class="health-panel" aria-labelledby="health-summary-title">
      <div class="overall-health tone-${toneClass(model.overall.tone)}">
        <strong>${formatScore(model.overall.score)}</strong>
        <i aria-hidden="true"></i>
        <span id="health-summary-title">Overall Health</span>
        <em>${escapeHtml(overallStatus(model))}</em>
      </div>
    </section>
  `;
}

function renderOperationalAreas(model: MspHealthPrototypeModel): string {
  return `
    <section class="content-section" aria-labelledby="operational-areas-title">
      <div class="section-title">
        <h2 id="operational-areas-title">Operational Areas</h2><span>Live signals and planned modules</span>
      </div>
      <div class="category-grid operational-area-grid">
        ${model.categories.map((category) => renderCategory(category, model)).join('')}
      </div>
    </section>
  `;
}

function renderCategory(category: PrototypeScoreCard, model: MspHealthPrototypeModel): string {
  const route = categoryRoute(category.label);
  const status = category.statusLabel ?? (category.dataKind === 'PLACEHOLDER' ? 'Planned' : formatScore(category.score));
  return `
    <a class="category-summary tone-${category.dataKind === 'PLACEHOLDER' ? 'neutral' : toneClass(category.tone)}" href="${route}" data-app-route>
      <div class="category-label">
        <span>${escapeHtml(category.label)}</span>
        <small>${escapeHtml(category.dataKind)}</small>
      </div>
      <strong>${status}</strong>
      <p>${escapeHtml(categorySummaryText(category, model))}</p>
      <span class="module-link">Open ${escapeHtml(category.label.replace(' Health', ''))}</span>
    </a>
  `;
}

function categorySummaryText(
  category: PrototypeScoreCard,
  model: MspHealthPrototypeModel,
): string {
  if (!/backup/i.test(category.label)) return category.supportingText;
  const healthy = model.clients.reduce((total, client) => total + client.healthyCount, 0);
  const attention = model.clients.reduce((total, client) => total + client.degradedCount, 0);
  const failed = model.clients.reduce((total, client) => total + client.failedCount, 0);
  return [
    `${healthy} healthy`,
    attention > 0 ? `${attention} attention` : '',
    failed > 0 ? `${failed} failed` : '',
  ].filter(Boolean).join(' · ');
}

function renderBackupHealthSummary(model: MspHealthPrototypeModel): string {
  const backup = backupDomain(model);
  return `
    <section class="health-panel backup-health-panel" aria-labelledby="backup-health-title">
      <div class="overall-health tone-${toneClass(backup.tone)}">
        <strong>${formatScore(backup.score)}</strong>
        <i aria-hidden="true"></i>
        <span id="backup-health-title">Observed Backup Health</span>
        <em>${escapeHtml(backup.dataKind)}</em>
      </div>
      <p class="backup-health-context">${escapeHtml(backup.supportingText)}</p>
    </section>
  `;
}

function renderIssues(issues: readonly PrototypeIssue[], title = 'Needs Attention'): string {
  return `
    <section class="content-section" id="issues" aria-labelledby="issues-title">
      <div class="section-title">
        <h2 id="issues-title">${escapeHtml(title)}</h2><span>(${issues.length})</span>
      </div>
      <div class="issue-table">
        ${issues.map(renderIssue).join('')}
      </div>
    </section>
  `;
}

function renderIssue(issue: PrototypeIssue): string {
  const tone = issue.severity === 'FAILED' ? 'critical' : 'attention';
  const label = issue.severity === 'FAILED' ? 'Failed' : 'Attention';
  const description = `<b>${escapeHtml(issue.deviceName)}</b> — ${escapeHtml(issue.summary)}`;
  const linkedDescription = issue.ninjaDeviceUrl === null
    ? `<span class="issue-description">${description}</span>`
    : `<a class="issue-description" href="${escapeAttribute(issue.ninjaDeviceUrl)}" target="_blank" rel="noopener noreferrer">${description}</a>`;
  return `
    <article class="issue-row tone-${tone}">
      <span class="status-dot" aria-hidden="true"></span>
      <strong class="issue-severity">${label}</strong>
      <span class="issue-client">${escapeHtml(issue.clientName)}</span>
      <span class="issue-detail">${linkedDescription}</span>
    </article>
  `;
}

function renderBackupClients(clients: readonly PrototypeClientCard[], managedClientCount: number): string {
  return `
    <section class="content-section" id="clients" aria-labelledby="clients-title">
      <div class="section-title">
        <h2 id="clients-title">Client Backup Health</h2><span>${managedClientCount} managed clients · problems first</span>
      </div>
      <div class="client-grid">
        ${clients.map(renderClient).join('')}
      </div>
    </section>
  `;
}

function renderManagedClients(clients: readonly PrototypeClientCard[], managedClientCount: number): string {
  return `
    <section class="content-section client-directory" aria-labelledby="managed-clients-title">
      <div class="section-title">
        <h2 id="managed-clients-title">Managed Clients</h2><span>${managedClientCount} clients</span>
      </div>
      <div class="client-grid">
        ${clients.map(renderManagedClient).join('')}
      </div>
    </section>
  `;
}

function renderManagedClient(client: PrototypeClientCard): string {
  return `
    <button class="client-card managed-client-card tone-${toneClass(client.tone)}" type="button" data-client-id="${client.organizationId}" aria-label="Open ${escapeAttribute(client.clientName)} client details">
      <span class="client-head">
        <span><strong>${escapeHtml(client.clientName)}</strong><small>${client.monitoredDeviceCount} monitored backup devices</small></span>
        <span class="client-score" title="Observed Backup Health"><i aria-hidden="true"></i><strong>${formatScore(client.backupScore)}</strong></span>
      </span>
      <span class="client-domain-list">
        <span><b>Backups</b><small>${client.failedCount > 0 ? `${client.failedCount} failed` : client.degradedCount > 0 ? `${client.degradedCount} attention` : 'Healthy'}</small></span>
        <span><b>Servers</b><small>Planned</small></span>
        <span><b>Microsoft 365</b><small>Planned</small></span>
      </span>
      <span class="client-entry-label">Open client</span>
    </button>
  `;
}

function renderClient(client: PrototypeClientCard): string {
  const destinations = [renderDestination(client.local), renderDestination(client.cloud)]
    .filter((row) => row.length > 0)
    .join('');
  return `
    <button class="client-card tone-${toneClass(client.tone)}" type="button" data-client-id="${client.organizationId}" aria-label="Open ${escapeAttribute(client.clientName)} observed backup health details">
      <span class="client-head">
        <span><strong>${escapeHtml(client.clientName)}</strong><small>${client.monitoredDeviceCount} monitored devices</small></span>
        <span class="client-score" title="Observed Backup Health"><i aria-hidden="true"></i><strong>${formatScore(client.backupScore)}</strong></span>
      </span>
      <span class="client-counts">
        <span>${client.healthyCount} healthy</span>
        <span>${client.degradedCount} attention</span>
        <span>${client.failedCount} failed</span>
      </span>
      ${destinations.length === 0 ? '' : `<span class="destination-list">${destinations}</span>`}
      ${(client.conditionNotes ?? []).map((note) => `<span class="client-condition-note"><b>${escapeHtml(note.deviceName)}</b> — ${escapeHtml(note.reason)}</span>`).join('')}
    </button>
  `;
}

function renderDestination(destination: PrototypeDestinationSummary): string {
  if (destination.observedCount === 0) return '';
  const coverage = destination.currentCount === destination.observedCount
    ? `${destination.currentCount} current`
    : `${destination.currentCount} of ${destination.observedCount} current`;
  return `<span class="destination-row"><span>${titleCase(destination.destination)}</span><strong>${coverage}</strong></span>`;
}

function formatScore(score: number | null): string {
  return score === null ? '—' : `${score}%`;
}

function backupDomain(model: MspHealthPrototypeModel): PrototypeScoreCard {
  return model.categories.find((category) => (
    category.label === 'Observed Backup Health' || category.label === 'Backup Health'
  )) ?? model.overall;
}

function overallStatus(model: MspHealthPrototypeModel): string {
  if (model.overall.score === null) return 'Not enough data';
  if (model.overall.tone === 'CRITICAL') return 'Action required';
  if (model.overall.tone === 'ATTENTION') return 'Needs attention';
  return 'All good';
}

function categoryRoute(label: string): string {
  if (/backup/i.test(label)) return '/backups';
  if (/microsoft 365/i.test(label)) return '/m365';
  if (/server/i.test(label)) return '/servers';
  if (/3cx/i.test(label)) return '/3cx';
  if (/onboarding/i.test(label)) return '/onboarding';
  return '/projects';
}

function titleCase(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

function toneClass(tone: PrototypeHealthTone): string {
  return tone.toLowerCase();
}

function icon(name: string): string {
  const paths: Record<string, string> = {
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
    overview: '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>',
    backup: '<path d="M5 8a7 7 0 1 1 1.2 9.9"/><path d="M5 3v5h5"/>',
    cloud: '<path d="M17.5 19H7a5 5 0 1 1 1-9.9A7 7 0 0 1 21 12.5 3.5 3.5 0 0 1 17.5 19Z"/>',
    server: '<rect x="3" y="4" width="18" height="6" rx="1"/><rect x="3" y="14" width="18" height="6" rx="1"/><path d="M7 7h.01M7 17h.01"/>',
    phone: '<path d="M6.6 10.8a15.5 15.5 0 0 0 6.6 6.6l2.2-2.2a1.5 1.5 0 0 1 1.5-.36c.95.32 1.96.5 3.01.5A1.1 1.1 0 0 1 21 16.44V20a1 1 0 0 1-1 1C10.61 21 3 13.39 3 4a1 1 0 0 1 1-1h3.56a1.1 1.1 0 0 1 1.1 1.09c0 1.06.17 2.07.5 3.02a1.5 1.5 0 0 1-.37 1.5l-2.19 2.19Z"/>',
    onboarding: '<path d="M12 3v18M3 12h18"/><circle cx="12" cy="12" r="9"/>',
    projects: '<path d="M4 7h16v13H4z"/><path d="M8 7V4h8v3"/>',
    clients: '<path d="M4 21v-7h7v7M13 21V3h7v18M7 17h.01M16 7h.01M16 11h.01M16 15h.01"/>',
    settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1 1.55V21h-4v-.08a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1H3v-4h.08a1.7 1.7 0 0 0 1.55-1 1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.55V3h4v.08a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 1.55 1H21v4h-.08a1.7 1.7 0 0 0-1.52 1Z"/>',
    security: '<path d="M12 3 4.5 6v5.5c0 4.6 3.1 7.9 7.5 9.5 4.4-1.6 7.5-4.9 7.5-9.5V6L12 3Z"/><path d="M9.5 12 11 13.5l3.5-4"/>',
  };
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name] ?? ''}</svg>`;
}
